import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import AuthLayout from "./AuthLayout";
import { supabase } from "../../../supabase/supabase";
import { useAuth } from "../../../supabase/auth";

export default function InviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [invitation, setInvitation] = useState<any>(null);
  const [registrationMode, setRegistrationMode] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Invalid invitation link");
      return;
    }

    const fetchInvitation = async () => {
      try {
        // Use the new check_invitation edge function
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check_invitation`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ token }),
          },
        );

        const result = await response.json();

        if (!response.ok) {
          // Handle different error types
          if (result.status === "expired") {
            throw new Error("This invitation has expired");
          } else if (result.status === "not_found") {
            throw new Error("Invalid invitation link");
          } else if (
            result.status === "cancelled" ||
            result.status === "accepted"
          ) {
            throw new Error(
              `This invitation has already been ${result.status}`,
            );
          } else {
            throw new Error(result.error || "Invalid or expired invitation");
          }
        }

        setInvitation(result.invitation);

        // Set email from invitation
        if (result.invitation.email) {
          setEmail(result.invitation.email);
        }
      } catch (error) {
        console.error("Error fetching invitation:", error);
        setError(error.message || "Failed to validate invitation");
      }
    };

    fetchInvitation();
    // Checked once per token only: signing in after "Create account & Join" changes `user`, and
    // re-checking then finds the invitation already accepted and shows an error
  }, [token]);

  // Check if user is already logged in
  useEffect(() => {
    if (user) {
      setFullName((name) => name || user.user_metadata?.full_name || "");
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (!invitation) {
      setError("Invalid invitation");
      setIsLoading(false);
      return;
    }

    try {
      // CASE 1: User is not logged in and needs to register
      if (!user && registrationMode) {
        // Validate password
        if (password !== confirmPassword) {
          setError("Passwords do not match");
          setIsLoading(false);
          return;
        }

        if (password.length < 6) {
          setError("Password must be at least 6 characters");
          setIsLoading(false);
          return;
        }

        try {
          // Instead of directly creating the account, we'll use our custom signup edge function
          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/signup_and_accept_invitation`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                email: invitation.email,
                password: password,
                fullName: fullName,
                invitationToken: token,
              }),
            },
          );

          const result = await response.json();

          if (!response.ok) {
            throw new Error(
              result.error || "Failed to create account and accept invitation",
            );
          }

          // If successful, try to automatically sign in the user
          const { error: signInError } = await supabase.auth.signInWithPassword(
            {
              email: invitation.email,
              password: password,
            },
          );

          if (signInError) {
            // If auto-signin fails, at least we created the account and accepted the invitation
            console.log(
              "Auto sign-in failed, but account was created:",
              signInError,
            );
            toast({
              title: "Account Created",
              description:
                "Your account has been created and you've joined the organization. Please sign in now.",
              duration: 5000,
            });

            // Redirect to login with a return URL
            navigate(`/login?redirect=/dashboard`, { replace: true });
            return;
          }

          // Show success message if everything worked
          toast({
            title: "Success!",
            description: `Your account has been created and you've joined ${result.organization?.name || "the organization"}.`,
            duration: 5000,
          });

          // Already signed in: straight to the dashboard
          navigate("/dashboard", { replace: true });
          return;
        } catch (error) {
          console.error("Error in signup process:", error);
          setError(error.message || "Failed to complete signup process");
          setIsLoading(false);
          return;
        }
      }

      // CASE 2: User is not logged in and wants to sign in
      if (!user && !registrationMode) {
        setRegistrationMode(true);
        setIsLoading(false);
        return;
      }

      // CASE 3: User is logged in but with wrong email
      if (user && user.email.toLowerCase() !== invitation.email.toLowerCase()) {
        setError(
          `This invitation was sent to ${invitation.email}, but you're logged in with ${user.email}. Please log out and sign in with the correct email.`,
        );
        setIsLoading(false);
        return;
      }

      // CASE 4: User is logged in with correct email - proceed with accepting invitation
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData.session;

      if (!session) {
        throw new Error("No active session");
      }

      // Call the accept_invitation edge function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/accept_invitation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            token,
            fullName:
              fullName ||
              user.user_metadata?.full_name ||
              user.email.split("@")[0],
          }),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to accept invitation");
      }

      // Show success message
      toast({
        title: "Success!",
        description: `You have successfully joined ${result.organization?.name || "the organization"}.`,
        duration: 5000,
      });

      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error("Error accepting invitation:", error);
      setError(error.message || "Error accepting invitation");
      setIsLoading(false);
    }
  };

  if (error && !invitation) {
    return (
      <AuthLayout>
        <div className="bg-card border border-border shadow-md p-8 w-full max-w-md mx-auto rounded-none">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground mb-4">
              Invitation Error
            </h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button
              onClick={() => navigate("/login")}
              className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/80 text-sm font-medium"
            >
              Go to Login
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="bg-card border border-border shadow-md p-8 w-full max-w-md mx-auto rounded-none">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-foreground">
            Accept Invitation
          </h2>
          {invitation && (
            <p className="text-muted-foreground mt-2">
              You've been invited to join{" "}
              {invitation.organization_name || "an organization"} as a{" "}
              {invitation.role}.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email info */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label className="text-sm font-medium text-foreground">
                Invitation sent to
              </Label>
              <span className="text-sm font-medium text-foreground">
                {email}
              </span>
            </div>
          </div>

          {/* Not logged in, not in registration mode */}
          {!user && !registrationMode && (
            <div className="space-y-6">
              <div className="p-3 bg-muted rounded-md">
                <div className="text-sm">
                  <p className="font-medium text-foreground">
                    Join {invitation?.organization_name || "this organization"}
                  </p>
                  <p className="text-muted-foreground mt-1">
                    You can create a new account with {email} or sign in with an
                    existing account.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() =>
                    navigate(
                      `/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`,
                    )
                  }
                >
                  Sign in
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/80"
                >
                  Create account
                </Button>
              </div>
            </div>
          )}

          {/* Not logged in, in registration mode */}
          {!user && registrationMode && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="fullName"
                  className="text-sm font-medium text-foreground"
                >
                  Full Name
                </Label>
                <Input
                  id="fullName"
                  placeholder="Your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="h-12 bg-background border-border focus:border-primary focus:ring-0"
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="password"
                  className="text-sm font-medium text-foreground"
                >
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 bg-background border-border focus:border-primary focus:ring-0"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Password must be at least 6 characters
                </p>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="confirmPassword"
                  className="text-sm font-medium text-foreground"
                >
                  Confirm Password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-12 bg-background border-border focus:border-primary focus:ring-0"
                />
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <div className="flex gap-4 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setRegistrationMode(false)}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/80"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating account..." : "Create account & Join"}
                </Button>
              </div>

              <div className="text-sm text-center text-muted-foreground mt-4">
                Already have an account?{" "}
                <a
                  href={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                  className="text-primary hover:text-primary/80 font-medium"
                >
                  Sign in
                </a>
              </div>
            </div>
          )}

          {/* Logged in with wrong email */}
          {user && user.email.toLowerCase() !== email.toLowerCase() && (
            <div className="space-y-4">
              <div className="p-3 bg-destructive/10 rounded-md">
                <div className="text-sm text-destructive">
                  <p className="font-medium">Email mismatch</p>
                  <p className="mt-1">
                    You're signed in as {user.email} but this invitation is for{" "}
                    {email}.
                  </p>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={async () => {
                  await supabase.auth.signOut();
                  window.location.reload();
                }}
              >
                Sign out
              </Button>
            </div>
          )}

          {/* Logged in with correct email */}
          {user && user.email.toLowerCase() === email.toLowerCase() && (
            <div className="space-y-4">
              <div className="p-3 bg-primary/10 rounded-md">
                <div className="text-sm text-primary">
                  <p className="font-medium">Ready to join</p>
                  <p className="mt-1">
                    You're signed in with the correct email address.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="fullName"
                  className="text-sm font-medium text-foreground"
                >
                  Your name (optional)
                </Label>
                <Input
                  id="fullName"
                  placeholder={user.user_metadata?.full_name || "Your name"}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="h-12 bg-background border-border focus:border-primary focus:ring-0"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leave blank to use your current name:{" "}
                  {user.user_metadata?.full_name || user.email.split("@")[0]}
                </p>
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <Button
                type="submit"
                className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/80 text-sm font-medium"
                disabled={isLoading}
              >
                {isLoading ? "Processing..." : "Accept Invitation & Join"}
              </Button>
            </div>
          )}
        </form>
      </div>
    </AuthLayout>
  );
}
